import "./paginationContainer.component.scss";
import { memo, useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Pagination } from "react-bootstrap";
import { cloneDeep } from "lodash";

// Const
import Nuclias from "const/nuclias";

// Component
import LinkerWithA from "../LinkerWithA";

// Util
import { setSortingHeader } from '../../utils/sorting.util';

const PaginationContainer = (props) => {
  const {
    paginationClassName,
    entryLimitClassName,
    previousLabel = 'dd1f775e44',
    nextLabel = '10ac3d0425',
    onPageChange,
    pageLimit = Nuclias.defaultPageLimit,
    onEntryLimitChange,
    isCurrentPageId = 1,
    total,
    marginTop = '20px'
  } = props;

  const { t } = useTranslation();

  const [currentPageId, setCurrentPageId] = useState(1);
  const [currentLimitId, setCurrentLimitId] = useState(pageLimit);
  const [totalPage, setTotalPage] = useState(0);
  const isPreviousLabel = !!previousLabel ? previousLabel : "Previous";
  const isNextLabel = !!nextLabel ? nextLabel : "Next";

  useEffect(() => {
    setTotalPage(Math.ceil(total / currentLimitId));
  }, [total, currentLimitId, totalPage]);

  useEffect(() => {
    setCurrentPageId(isCurrentPageId);
  }, [isCurrentPageId]);

  const changePageId = useCallback(
    (pageId) => {
      if (!Number.isInteger(pageId)) {
        let KeepCurrentPageId = currentPageId;
        if (pageId === "prev") {
          setCurrentPageId(KeepCurrentPageId - 1);
          onPageChange(KeepCurrentPageId - 1);
        }
        if (pageId === "next") {
          setCurrentPageId(KeepCurrentPageId + 1);
          onPageChange(KeepCurrentPageId + 1);
        }
      } else {
        setCurrentPageId(pageId);
        onPageChange(pageId);
      }
    },
    [currentPageId, onPageChange]
  );

  const changeLimitId = useCallback((limitId) => {
    setCurrentLimitId(limitId);
    onEntryLimitChange(limitId);
    setTotalPage(Math.ceil(total / limitId));
    setCurrentPageId(1);
  }, [onEntryLimitChange, total]);

  const PageNumbers = (ellipsisItems) => {
    let content = [];
    if (totalPage <= 10) {
      for (let i = 1; i <= totalPage; i++) {
        content.push(
          <Pagination.Item
            onClick={() => changePageId(i)}
            active={!!(+currentPageId === i)}
            key={i}
          >
            {i}
          </Pagination.Item>
        );
      }
    } else {
      content = ellipsisItems.map((item) => {
        if (item.type === "page") {
          return (
            <Pagination.Item
              onClick={() => changePageId(item.page)}
              active={!!(+currentPageId === item.page)}
              key={item.page}
            >
              {item.page}
            </Pagination.Item>
          );
        } else {
          return (
            <Pagination.Item
              className={`${"disabled pagination-disabled"}`}
              key={item.page}
            >
              ...
            </Pagination.Item>
          );
        }
      });
    }
    return content;
  };

  const items = [...Array(totalPage).keys()]
    .map((key) => key + 1)
    .map((item) => ({
      type: "page",
      isCurrent: currentPageId === item,
      page: item,
    }));

  const markedItems = items.map((item) => {
    if (
      item.page === totalPage ||
      item.page === 1 ||
      item.page === currentPageId ||
      item.page === currentPageId + 1 ||
      item.page === currentPageId + 2 ||
      item.page === currentPageId - 1 ||
      item.page === currentPageId - 2
    ) {
      return item;
    }
    return {
      ...item,
      type: item.page > currentPageId ? "end-ellipsis" : "start-ellipsis",
    };
  });

  const ellipsisItems = markedItems.filter((item, index) => {
    if (
      item.type === "start-ellipsis" &&
      markedItems[index + 1].type === "start-ellipsis"
    ) {
      return false;
    }
    if (
      item.type === "end-ellipsis" &&
      markedItems[index + 1].type === "end-ellipsis"
    ) {
      return false;
    }
    return true;
  });

  return (
    <div className="pagination-container" style={{ marginTop }}>
      <Pagination
        size="sm"
        className={`justify-content-end ${paginationClassName}`}
      >
        <Pagination.Prev
          onClick={() => {
            changePageId("prev");
          }}
          active={!!currentPageId === "prev"}
          className={`${currentPageId === 1 ? "disabled pagination-disabled" : ""
            }`}
        >
          {t(isPreviousLabel)}
        </Pagination.Prev>
        {PageNumbers(ellipsisItems)}
        <Pagination.Next
          onClick={() => changePageId("next")}
          active={!!currentPageId === "next"}
          className={`${currentPageId === totalPage || totalPage === 0
            ? "disabled pagination-disabled"
            : ""
            }`}
        >
          {t(isNextLabel)}
        </Pagination.Next>
      </Pagination>

      <div
        className={`pagination-dropdown-container dropdown ${entryLimitClassName}`}
      >
        <button
          className="btn dropdown-toggle"
          type="button"
          id="pagination-drpodown"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          {currentLimitId}
        </button>
        <ul className="dropdown-menu" aria-labelledby="pagination-drpodown">
          {Nuclias.pageLimits.map((limit, index) => {
            return (
              <li key={index}>
                <a
                  className={`dropdown-item ${!!(currentLimitId === limit) ? "active" : ""
                    }`}
                  href="#/"
                  onClick={() => changeLimitId(limit)}
                >
                  {limit}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export const getPageLimit = () => {
  return {
    list: cloneDeep(Nuclias.pageLimits),
    default: cloneDeep(Nuclias.defaultPageLimit),
  };
};

export const sliceDataByPage = (data, currentPage, pageLimit) => {
  if (pageLimit === 0) {
    return data;
  }

  return data.slice(
    (currentPage - 1) * pageLimit,
    (currentPage - 1) * pageLimit + pageLimit
  );
};

export const sortOrderType = {
  asc: 'asc',
  desc: 'desc'
};

export const renderTableHeader = (columnList, order, setOrder, sortField, setSortField, handleSorting) => {
  const handleSortingChange = (e, accessor) => {
    const sortOrder =
      accessor === sortField && order === sortOrderType.desc ? sortOrderType.asc : sortOrderType.desc;
    setSortField(accessor);
    setOrder(sortOrder);
    handleSorting(accessor, sortOrder);
    setSortingHeader(e);
  };

  return columnList.map(({ label, accessor, sortable }) => {
    const cl = sortable
      ? sortField === accessor && order === sortOrderType.asc
        ? 'is-asc'
        : sortField === accessor && order === sortOrderType.desc
          ? 'is-desc'
          : 'default'
      : '';
    return (
      <th key={accessor}>
        <LinkerWithA
          label={label}
          className={`text-decoration-none ${cl}`}
          key={accessor}
          onClick={
            sortable ? e => handleSortingChange(e, accessor) : null
          }
        />
      </th>
    );
  });
};

export default memo(PaginationContainer);


/**
Usage for render table and sort

- Step 1 import necessary componet、func and hook
  import PaginationContainer, { getPageLimit, sliceDataByPage, sortOrderType, renderTableHeader } from "/components/PaginationContainer";
  import { useSortableTable } from '/hooks/useSortableTable';

- Step 2, pass an array(your fetch from API) and column(you defined for table header) into hook
  const [tableData, handleSorting] = useSortableTable(data, columnList);

- Step 3, define sort field and order hook for sorting
  const [sortField, setSortField] = useState('');
  const [order, setOrder] = useState(sortOrderType.asc);

- Step 4, render table
  <Table responsive hover className='table-container'>
    <thead>
      <tr>
        <th>#</th>
        {renderTableHeader(columnList, order, setOrder, sortField, setSortField, handleSorting)}
      </tr>
    </thead>
    <tbody>
      {currentLicenseLogList && currentLicenseLogList.map((item, index) => {
        return (
          <tr key={index}>
            <td>{(selectedPage - 1) * selectedPageLimit + index + 1}</td>
            <td>{item.time}</td>
            <td>{item.account}</td>
            <td>{item.licenseKey}</td>
            <td>{item.action}</td>
            <td>{item.details}</td>
          </tr>
        )
      })}
    </tbody>
  </Table>
*/
